import { defineNuxtPlugin } from '#imports'
import { StatusType } from '@injectivelabs/utils/status'
import { isThrownException } from '@injectivelabs/exceptions'
import { localStorage } from '@/app/services/localStorage'
import {
  buildPersistedState,
  sanitizePersistedState
} from '@/utils/storePersistence'
import type { StateTree, PiniaPluginContext, SubscriptionCallback } from 'pinia'

const actionsThatSetAppStateToBusy = [
  'account/deposit',
  'account/withdraw',
  'position/cancelTp',
  'position/cancelSl',
  'position/applyTpSl',
  'account/withdrawToSafe',
  'account/withdrawToMain',
  'position/cancelAllTpSl',
  'rfq/submitToRfqExecutor',
  'account/externalTransfer',
  'referral/registerInvitee',
  'authZ/grantAuthorization',
  'authZ/revokeAuthorization',
  'authZ/revokeAuthorizations',
  'derivative/cancelDerivativeOrder',
  'derivative/submitDerivativeChase',
  'derivative/submitDerivativeLimitOrder',
  'derivative/submitDerivativeStopLimitOrder',
  'derivative/submitDerivativeStopMarketOrder',
  'derivative/batchCancelDerivativeOrder',
  'position/closeLimitPosition',
  'position/closeMarketPosition',
  'position/addMarginToPosition'
]

const actionsThatThrowErrors = ['token/fetchTokensUsdPriceMap']

const persistState: SubscriptionCallback<StateTree> = (mutation, state) => {
  const existingState = (localStorage.get('state') || {}) as any
  const updatedState = buildPersistedState({
    state,
    existingState,
    storeId: mutation.storeId
  })

  if (!updatedState) {
    return
  }

  localStorage.set('state', updatedState)
}

function piniaStoreSubscriber({ store, pinia }: PiniaPluginContext) {
  const localState = localStorage.get('state') as any

  const appStore = useAppStore(pinia)
  const sharedWalletStore = useSharedWalletStore(pinia)
  const { $onError } = useNuxtApp()

  if (localState?.[store.$id]) {
    store.$state = {
      ...store.$state,
      ...sanitizePersistedState(store.$id, localState[store.$id])
    }
  }

  // force reset busyActions to empty array on app store initialization
  if (store.$id === 'app') {
    appStore.setBusyActions([])
  }

  store.$subscribe(persistState)

  store.$onAction(({ name, store: { $id }, after, onError }) => {
    const type = `${$id}/${name}`
    const isActionThatSetsAppStateToBusy =
      actionsThatSetAppStateToBusy.includes(type)
    const shouldTrackBusyAction =
      isActionThatSetsAppStateToBusy &&
      (appStore.busyActions.length > 0 ||
        sharedWalletStore.queueStatus !== StatusType.Loading)

    if (shouldTrackBusyAction) {
      appStore.setBusyActions([...appStore.busyActions, type])
    }

    const clearBusyAction = () => {
      if (!shouldTrackBusyAction) {
        return
      }

      const busyActions = [...appStore.busyActions]
      const actionIndex = busyActions.lastIndexOf(type)

      if (actionIndex >= 0) {
        busyActions.splice(actionIndex, 1)
      }

      appStore.setBusyActions(busyActions)

      if (busyActions.length === 0) {
        sharedWalletStore.queueStatus = StatusType.Idle
      }
    }

    after(clearBusyAction)

    onError((error) => {
      clearBusyAction()

      if (
        actionsThatThrowErrors.includes(type) &&
        error instanceof Error &&
        isThrownException(error)
      ) {
        $onError(error)
      }
    })
  }, true)
}

// The 'name: "store"' is required for plugin ordering.
// The auth plugin depends on this plugin via `dependsOn: ['store']`
export default defineNuxtPlugin({
  name: 'store',

  setup: ({
    vueApp: {
      config: { globalProperties }
    }
  }) => {
    globalProperties.$pinia.use(piniaStoreSubscriber)
  }
})
